import { useState, useCallback, useEffect, useRef } from "react";
import { AssistantChatMessage, ChatMessage, UserChatMessage } from "../types";
import { generateUUID } from "../../../utils/uuid";
import { AI_BASE_URL, aiHeaders } from "../api/aiClient";

// ── Chat persistence ─────────────────────────────────────────────────────────
// Messages and the session id survive a page refresh via localStorage.
//
// Result ROWS are still stripped before saving, and deliberately so: up to 5,000
// per message against a ~5MB localStorage budget, PII-bearing, and stale the
// moment they are restored while still looking current. The summary, stats, SQL
// and insight chart -- all small -- are kept, so a restored chat reads complete.
// The missing table is now refillable on demand via rerunTurn, which re-runs
// that turn's stored SQL server-side rather than trusting a saved copy.
//
// The backend session store used to be an in-memory dict with a 30-minute TTL,
// so after a long gap or an API restart a follow-up like "again" lost its server
// context even though the transcript was still on screen. That is now persisted
// in llm_chat_session / llm_chat_turn and survives both.

const CHAT_V1_KEY  = "samiksha_chat_v1";     // single-chat era
const CHATS_V2_KEY = "samiksha_chats_v2";    // one key for the whole browser
const CHATS_KEY_PREFIX = "samiksha_chats_v3:";
const MAX_CHATS = 20;

/**
 * Where this user's conversations are stored in the browser.
 *
 * v1 and v2 used a single key for the whole browser, with nothing tying it to an
 * account. On a shared machine — which is most of them — the next person to sign
 * in opened the previous user's chats: their questions, summaries, stat cards
 * and SQL. Keying by user id stops the crossover, and authService.logout()
 * removes the key so the data does not simply sit there afterwards.
 *
 * This is browser-side only. Server-side history in llm_chat_session is scoped
 * by user_id and was never affected.
 */
function chatsKey(): string {
  return CHATS_KEY_PREFIX + (localStorage.getItem("username") || "anon");
}

export interface StoredChat {
  id: string;
  title: string;
  sessionId: string | null;
  updatedAt: number;
  messages: ChatMessage[];
  /** Listed by the server but its transcript has not been fetched yet.
   *  selectChat pulls it on demand rather than downloading every conversation
   *  on sign-in. */
  remote?: boolean;
}

/** Rebuild chat messages from a stored transcript. */
function messagesFromTurns(turns: Record<string, any>[]): ChatMessage[] {
  const out: ChatMessage[] = [];
  for (const t of turns) {
    out.push({ id: `t${t.turn_id}-q`, role: "user", query: t.query });
    out.push({
      id: `t${t.turn_id}-a`,
      role: "assistant",
      stage: t.stage === "error" ? "error" : t.stage === "clarify" ? "clarify" : "done",
      sqlQuery: t.sql_query || null,
      // Result rows are never stored. rerunTurn refills them from sql_query
      // when the user asks; turnId below is what makes that possible.
      data: null,
      rowCount: t.row_count ?? null,
      totalCount: null,
      visualizationConfig: t.visualization_config ?? null,
      summary: t.summary || null,
      downloadUrl: null,
      error: t.error_text ?? null,
      clarifyQuestion: t.clarify?.question ?? null,
      clarifyOptions: t.clarify?.options ?? [],
      insightChart: t.insight_chart ?? null,
      stats: t.stats ?? null,
      turnId: t.turn_id ?? null,
      rerunAt: null,
      rerunState: "idle",
      rerunError: null,
      // Reports and multi-part answers are rebuilt from live data, never
      // stored, so a restored turn keeps the narrative it was summarised with
      // but not the cards.
      report: null,
      message: null,
      followups: [],
      plannedParts: null,
      parts: null,
    } as ChatMessage);
  }
  return out;
}

/** Add server conversations this browser has not seen, newest first. */
function mergeServerSessions(
  local: StoredChat[],
  sessions: Record<string, any>[],
): StoredChat[] {
  const known = new Set(local.map((c) => c.sessionId).filter(Boolean));
  const added = sessions
    .filter((s) => !known.has(s.session_id))
    .map((s): StoredChat => ({
      id: s.session_id,
      title: s.title,
      sessionId: s.session_id,
      updatedAt: new Date(s.updated_at).getTime() || Date.now(),
      messages: [],
      remote: true,
    }));
  return [...local, ...added].sort((a, b) => b.updatedAt - a.updatedAt);
}

interface ChatsStore {
  chats: StoredChat[];      // newest first
  activeId: string | null;
}

function stripForStorage(messages: ChatMessage[]): ChatMessage[] {
  return messages.slice(-40).map((m) => {
    if (m.role !== "assistant") return m;
    const a = m as AssistantChatMessage;
    return {
      ...a,
      data: null,          // rows: large, PII, stale after refresh
      downloadUrl: null,   // temp-file token; dead after an API restart
      // Rows re-run into this message are dropped with everything else, so the
      // freshness marker has to go too -- keeping it would label an empty table
      // as recently loaded.
      rerunAt: null,
      rerunState: "idle" as const,
      rerunError: null,
      report: null,
      message: null,
      plannedParts: null,
      parts: null,
      // followups survive the strip deliberately: they are a few short strings
      // and still clickable after a reload, unlike rows or download tokens.
      // A message caught mid-stream must not rehydrate stuck on a spinner.
      stage: a.stage === "error" || a.stage === "clarify" ? a.stage : "done",
    } as ChatMessage;
  });
}

function sanitizeMessages(messages: ChatMessage[]): ChatMessage[] {
  return messages
    .filter((m) => m && (m.role === "user" || m.role === "assistant"))
    .map((m) => {
      if (m.role !== "assistant") return m;
      // Backfill fields added after a save was written -- the type claims they
      // exist; a stale JSON blob may disagree.
      const a = m as AssistantChatMessage;
      return {
        ...a,
        clarifyOptions: a.clarifyOptions ?? [],
        clarifyQuestion: a.clarifyQuestion ?? null,
        insightChart: a.insightChart ?? null,
        stats: a.stats ?? null,
        turnId: a.turnId ?? null,
        rerunAt: null,
        rerunState: "idle" as const,
        rerunError: null,
        report: null,
        message: null,
        followups: a.followups ?? [],
        plannedParts: null,
        parts: null,
        data: a.data ?? null,
        stage: a.stage === "error" || a.stage === "clarify" ? a.stage : "done",
      } as ChatMessage;
    });
}

function chatTitle(messages: ChatMessage[]): string {
  const firstUser = messages.find((m) => m.role === "user") as UserChatMessage | undefined;
  const q = firstUser?.query?.trim() || "New conversation";
  return q.length > 48 ? q.slice(0, 48) + "…" : q;
}

function loadStore(): ChatsStore {
  // The v1 and v2 saves carry no user id, so there is no honest way to decide
  // whose they are — migrating them would be a guess, and a wrong guess shows
  // one user another's conversations. Dropping them costs a local history list
  // once; the alternative is a data leak that looks like a working feature.
  try {
    localStorage.removeItem(CHAT_V1_KEY);
    localStorage.removeItem(CHATS_V2_KEY);
  } catch { /* storage unavailable */ }

  try {
    const raw = localStorage.getItem(chatsKey());
    if (raw) {
      const parsed = JSON.parse(raw) as ChatsStore;
      if (Array.isArray(parsed.chats)) {
        const chats = parsed.chats
          .filter((c) => c && c.id && Array.isArray(c.messages))
          .map((c) => ({ ...c, messages: sanitizeMessages(c.messages) }));
        const activeId = chats.some((c) => c.id === parsed.activeId) ? parsed.activeId : null;
        return { chats, activeId };
      }
    }
  } catch { /* corrupted storage -- start fresh */ }
  return { chats: [], activeId: null };
}

function persistStore(store: ChatsStore): void {
  const slim: ChatsStore = {
    activeId: store.activeId,
    chats: store.chats.slice(0, MAX_CHATS).map((c) => ({
      ...c,
      messages: stripForStorage(c.messages),
    })),
  };
  const key = chatsKey();
  try {
    localStorage.setItem(key, JSON.stringify(slim));
  } catch {
    // Quota exceeded -- keep only the five most recent conversations.
    try {
      localStorage.setItem(
        key,
        JSON.stringify({ ...slim, chats: slim.chats.slice(0, 5) }),
      );
    } catch { /* persistence unavailable */ }
  }
}

export function useAnalyticsChatSSE() {
  // The ACTIVE conversation's messages live in their own state so streaming
  // patches stay cheap; the chats list carries every conversation for the
  // sidebar, including the active one (synced by the effect below).
  const initial = useRef(loadStore()).current;
  const [chats, setChats] = useState<StoredChat[]>(initial.chats);
  const [activeChatId, setActiveChatId] = useState<string | null>(initial.activeId);
  const [messages, setMessages] = useState<ChatMessage[]>(
    () => initial.chats.find((c) => c.id === initial.activeId)?.messages ?? []
  );
  const [isRunning,  setIsRunning]  = useState(false);
  const abortRef   = useRef<AbortController | null>(null);
  const sessionsLoadedRef = useRef(false);
  const sessionRef = useRef<string | null>(
    initial.chats.find((c) => c.id === initial.activeId)?.sessionId ?? null
  );

  /**
   * Pull this user's conversations from the server. Call once, from the screen
   * that shows them.
   *
   * Chat history used to live only in localStorage, so signing in on a new
   * machine — or a fresh browser on the same one — showed an empty sidebar even
   * though every turn was already stored against the account. Only titles come
   * down here; each transcript is fetched when its chat is opened.
   *
   * NOT a mount effect: AnalyticsChatProvider wraps ProtectedRoute, so this hook
   * is alive on every authenticated page. Fetching on mount meant every screen
   * in the app requested the chat list whether or not anything showed it.
   */
  const loadSessions = useCallback(async () => {
    if (sessionsLoadedRef.current) return;
    sessionsLoadedRef.current = true;
    try {
      const response = await fetch(
        `${AI_BASE_URL}/api/v1/analytics/sessions`, { headers: aiHeaders() },
      );
      if (!response.ok) return;
      const payload = await response.json();
      const sessions = payload.sessions ?? [];
      if (sessions.length === 0) return;
      setChats((prev) => {
        const merged = mergeServerSessions(prev, sessions);
        persistStore({ chats: merged, activeId: null });
        return merged;
      });
    } catch {
      // Offline, or an API that predates /sessions. The local list still works.
    }
  }, []);

  // Sync the active conversation into the chats list and persist everything.
  // A conversation is created LAZILY on its first message, so "New chat" never
  // litters the sidebar with empty entries.
  useEffect(() => {
    setChats((prev) => {
      let next: StoredChat[];
      if (messages.length === 0) {
        next = prev;
      } else if (activeChatId && prev.some((c) => c.id === activeChatId)) {
        next = prev.map((c) =>
          c.id === activeChatId
            ? { ...c, messages, title: chatTitle(messages), sessionId: sessionRef.current, updatedAt: Date.now() }
            : c
        );
        // most recently touched first
        next = [...next].sort((a, b) => b.updatedAt - a.updatedAt);
      } else {
        const id = activeChatId ?? generateUUID();
        if (!activeChatId) setActiveChatId(id);
        next = [
          { id, title: chatTitle(messages), sessionId: sessionRef.current, updatedAt: Date.now(), messages },
          ...prev,
        ];
      }
      persistStore({ chats: next, activeId: activeChatId ?? (next[0]?.id ?? null) });
      return next;
    });
  }, [messages, activeChatId]);

  const runQuery = useCallback(async (
    queryText: string,
    edit?: { turnId: number | null },
  ) => {
    abortRef.current?.abort();

    // An edit replaces an earlier question, so the server has to drop that turn
    // and every turn after it -- that is what edit_turn_id does. With no turn id
    // it cannot, and keeping the session would leave the model reading a branch
    // the user just deleted. Starting a clean session loses the earlier context
    // but never answers against a conversation that no longer exists.
    if (edit && edit.turnId == null) sessionRef.current = null;
    const controller = new AbortController();
    abortRef.current = controller;

    const userMsg: UserChatMessage = {
      id: generateUUID(),
      role: "user",
      query: queryText,
    };
    const asstId = generateUUID();
    const asstMsg: AssistantChatMessage = {
      id: asstId, role: "assistant", stage: "thinking",
      sqlQuery: null, data: null, rowCount: null, totalCount: null,
      visualizationConfig: null, summary: null, downloadUrl: null, error: null,
      clarifyQuestion: null, clarifyOptions: [], insightChart: null, stats: null,
      turnId: null, rerunAt: null, rerunState: "idle", rerunError: null,
      report: null, message: null, followups: [], plannedParts: null, parts: null,
    };

    setMessages(prev => [...prev, userMsg, asstMsg]);
    setIsRunning(true);

    // Patch the specific assistant message by id — safe with functional updates
    const patch = (update: Partial<AssistantChatMessage>) =>
      setMessages(prev => prev.map(m =>
        m.id === asstId ? { ...m, ...update } as ChatMessage : m
      ));

    try {
      const response = await fetch(`${AI_BASE_URL}/api/v1/analytics/query`, {
        method: "POST",
        headers: aiHeaders(),
        body: JSON.stringify({
          query:        queryText,
          session_id:   sessionRef.current ?? undefined,
          edit_turn_id: edit?.turnId ?? undefined,
        }),
        signal: controller.signal,
      });

      if (!response.ok) {
        const err = await response.json().catch(() => ({ detail: response.statusText }));
        const detail = err.detail;
        const errMsg = typeof detail === "string"
          ? detail
          : Array.isArray(detail)
            ? detail.map((e: { msg?: string }) => e.msg ?? JSON.stringify(e)).join("; ")
            : "Request failed";
        patch({ stage: "error", error: errMsg });
        setIsRunning(false);
        return;
      }

      const reader = response.body?.getReader();
      if (!reader) {
        patch({ stage: "error", error: "No response stream" });
        setIsRunning(false);
        return;
      }

      const decoder = new TextDecoder();
      let buffer = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });

        const blocks = buffer.split("\n\n");
        buffer = blocks.pop() ?? "";

        for (const block of blocks) {
          const lines = block.trim().split("\n");
          let eventName = "", dataStr = "";
          for (const line of lines) {
            if (line.startsWith("event: ")) eventName = line.slice(7).trim();
            if (line.startsWith("data: "))  dataStr   = line.slice(6).trim();
          }
          if (!eventName || !dataStr) continue;

          try {
            const payload = JSON.parse(dataStr);
            switch (eventName) {
              case "sql":
                patch({ stage: "sql", sqlQuery: payload.sql_query as string });
                break;
              case "data":
                if (payload.session_id) sessionRef.current = payload.session_id as string;
                patch({
                  stage:               "data",
                  data:                payload.data as Record<string, unknown>[],
                  rowCount:            payload.row_count   as number,
                  totalCount:          (payload.total_count  as number)  ?? null,
                  downloadUrl:         (payload.download_url as string)  ?? null,
                });
                break;
              case "summary":
                patch({
                  stage:               "done",
                  turnId:              (payload.turn_id as number) ?? null,
                  followups:           (payload.followups as string[]) ?? [],
                  summary:             payload.summary as string,
                  visualizationConfig: payload.visualization_config as AssistantChatMessage["visualizationConfig"],
                  insightChart:        (payload.insight_chart as AssistantChatMessage["insightChart"]) ?? null,
                  stats:               (payload.stats as AssistantChatMessage["stats"]) ?? null,
                });
                break;
              case "clarify":
                // Terminal event: the copilot asked a question instead of
                // answering. It carries session_id because the "data" event that
                // normally supplies it never fires -- without storing it here the
                // user's reply would open a new session that has forgotten the
                // question it is replying to.
                if (payload.session_id) sessionRef.current = payload.session_id as string;
                patch({
                  stage:           "clarify",
                  turnId:          (payload.turn_id as number) ?? null,
                  clarifyQuestion: payload.question as string,
                  clarifyOptions:  (payload.options as string[]) ?? [],
                });
                break;
              // Terminal: a greeting or capability question, answered without
              // touching the database. No SQL, no table, no stored turn.
              case "message":
                patch({ stage: "done", message: payload.message as string });
                break;
              // Not terminal: the planner has decided the question is really
              // two, and names them so the wait has something to show.
              case "plan":
                patch({ plannedParts: (payload.parts as string[]) ?? null });
                break;
              // Terminal: each sub-question answered on its own, with its own
              // SQL. Nothing here is added across parts.
              case "parts":
                if (payload.session_id) sessionRef.current = payload.session_id as string;
                patch({
                  stage:  "done",
                  turnId: (payload.turn_id as number) ?? null,
                  parts:  payload.parts as AssistantChatMessage["parts"],
                });
                break;
              // Terminal, like clarify: a whole-school report replaces the
              // sql/data/summary sequence, so nothing further arrives.
              case "report":
                if (payload.session_id) sessionRef.current = payload.session_id as string;
                patch({
                  stage:   "done",
                  turnId:  (payload.turn_id as number) ?? null,
                  summary: payload.summary as string,
                  report:  payload.report as AssistantChatMessage["report"],
                });
                break;
              case "error":
                patch({
                  stage:  "error",
                  turnId: (payload.turn_id as number) ?? null,
                  error: (payload.detail as string) || (payload.message as string) || "An error occurred",
                });
                break;
            }
          } catch { /* malformed JSON — skip */ }
        }
      }

      // Mark done if not already in error
      setMessages(prev => prev.map(m =>
        m.id === asstId
        && (m as AssistantChatMessage).stage !== "error"
        && (m as AssistantChatMessage).stage !== "clarify"
          ? { ...m, stage: "done" } as ChatMessage
          : m
      ));
    } catch (err: unknown) {
      if ((err as Error).name === "AbortError") return;
      patch({ stage: "error", error: (err as Error).message || "Unknown error" });
    } finally {
      setIsRunning(false);
    }
  }, []);

  const sendQuery = useCallback(
    (queryText: string) => runQuery(queryText),
    [runQuery],
  );

  /**
   * Reword an earlier question and ask it again.
   *
   * Everything from that question downwards is dropped -- those answers replied
   * to a question that no longer exists. The edit handle comes from the
   * assistant message that answered it; runQuery falls back to a fresh session
   * when there is none, so the server can never keep a branch the user removed.
   */
  const editQuery = useCallback((userMessageId: string, newText: string) => {
    const text = newText.trim();
    if (!text) return;

    const index = messages.findIndex((m) => m.id === userMessageId);
    if (index === -1) return;

    const answer = messages[index + 1];
    const turnId = answer && answer.role === "assistant"
      ? (answer as AssistantChatMessage).turnId ?? null
      : null;

    setMessages(messages.slice(0, index));
    void runQuery(text, { turnId });
  }, [messages, runQuery]);

  /**
   * Refill an old answer's table by re-running its stored SQL.
   *
   * Result rows are never saved anywhere — thousands of student and staff
   * records per turn, which would age into stale data presented as current — so
   * a reopened chat shows the summary, stats and SQL but no table. This asks the
   * server to run that turn's query again. The server re-validates it against
   * the caller's CURRENT scope first, so a saved query cannot outlive the
   * permissions it was written under.
   *
   * Figures may differ from the summary above them; rerunAt records when these
   * rows were actually read so the UI can say so.
   */
  const rerunTurn = useCallback(async (messageId: string) => {
    const target = messages.find((m) => m.id === messageId);
    if (!target || target.role !== "assistant") return;
    const answer = target as AssistantChatMessage;
    if (answer.turnId == null || answer.rerunState === "loading") return;

    const patch = (update: Partial<AssistantChatMessage>) =>
      setMessages((prev) => prev.map((m) =>
        m.id === messageId ? { ...m, ...update } as ChatMessage : m
      ));

    patch({ rerunState: "loading", rerunError: null });

    try {
      // GET, not POST: the API blocks every POST that is not explicitly
      // whitelisted in its ReadOnlyMethodMiddleware, and reloading a stored
      // result set mutates nothing.
      const response = await fetch(
        `${AI_BASE_URL}/api/v1/analytics/turn/${answer.turnId}/results`,
        { headers: aiHeaders() },
      );

      if (!response.ok) {
        const body = await response.json().catch(() => null);
        const detail = typeof body?.detail === "string" ? body.detail : null;
        patch({
          rerunState: "error",
          rerunError: detail ?? (
            response.status === 404
              ? "These results are no longer available."
              : "Could not reload the results."
          ),
        });
        return;
      }

      const payload = await response.json();
      const viz = payload.visualization_config as AssistantChatMessage["visualizationConfig"];
      patch({
        data:        payload.data as Record<string, unknown>[],
        rowCount:    payload.row_count as number,
        totalCount:  (payload.total_count as number) ?? null,
        downloadUrl: (payload.download_url as string) ?? null,
        // Replayed from storage so the reloaded table looks like the one the
        // user remembers; keep the existing config if the server had none.
        visualizationConfig: viz ?? answer.visualizationConfig,
        rerunAt:     Date.now(),
        rerunState:  "idle",
        rerunError:  null,
      });
    } catch (err: unknown) {
      patch({
        rerunState: "error",
        rerunError: (err as Error).message || "Could not reload the results.",
      });
    }
  }, [messages]);

  // "New chat": park the current conversation (already synced by the effect)
  // and start clean. The new entry is created lazily on the first message.
  const reset = useCallback(() => {
    abortRef.current?.abort();
    sessionRef.current = null;
    setActiveChatId(null);
    setMessages([]);
    setIsRunning(false);
  }, []);

  const selectChat = useCallback(async (id: string) => {
    abortRef.current?.abort();
    setIsRunning(false);

    const target = chats.find((c) => c.id === id);
    if (!target) return;

    sessionRef.current = target.sessionId;
    setActiveChatId(id);
    setMessages(target.messages);
    persistStore({ chats, activeId: id });

    // Listed from the server but never opened on this browser — pull the
    // transcript now. Fetching every conversation at sign-in would download a
    // lot to show a sidebar of titles.
    if (target.messages.length > 0 || !target.sessionId) return;
    try {
      const response = await fetch(
        `${AI_BASE_URL}/api/v1/analytics/sessions/${target.sessionId}`,
        { headers: aiHeaders() },
      );
      if (!response.ok) return;
      const payload = await response.json();
      const restored = messagesFromTurns(payload.turns ?? []);
      if (restored.length === 0) return;
      setMessages(restored);
      setChats((prev) => prev.map((c) =>
        c.id === id ? { ...c, messages: restored, remote: false } : c
      ));
    } catch { /* offline — the empty conversation is still selectable */ }
  }, [chats]);

  /**
   * Delete one conversation, locally and on the server.
   *
   * Found 2026-08-24: this used to only filter the local list, so the
   * session survived in llm_chat_session and came back to life the next
   * time /sessions was fetched — a "deleted" chat that quietly reappeared.
   * The DELETE call is fire-and-forget: the sidebar already reflects the
   * removal, and a network failure just means it resurfaces next load
   * rather than leaving the UI stuck waiting on it.
   */
  const deleteChat = useCallback((id: string) => {
    const target = chats.find((c) => c.id === id);
    if (target?.sessionId) {
      fetch(`${AI_BASE_URL}/api/v1/analytics/sessions/${target.sessionId}`, {
        method: "DELETE",
        headers: aiHeaders(),
      }).catch(() => {});
    }
    setChats((prev) => {
      const next = prev.filter((c) => c.id !== id);
      const stillActive = id !== activeChatId;
      if (!stillActive) {
        abortRef.current?.abort();
        sessionRef.current = null;
        setActiveChatId(null);
        setMessages([]);
        setIsRunning(false);
      }
      persistStore({ chats: next, activeId: stillActive ? activeChatId : null });
      return next;
    });
  }, [activeChatId, chats]);

  return { messages, isRunning, sendQuery, editQuery, rerunTurn, loadSessions, reset, chats, activeChatId, selectChat, deleteChat };
}
